"use client";

import { useEffect, useMemo, useRef } from "react";
import {
  createChart,
  AreaSeries,
  LineSeries,
  ColorType,
  CrosshairMode,
  LineStyle,
  type IChartApi,
  type ISeriesApi,
  type Time,
} from "lightweight-charts";
import { useTheme } from "next-themes";
import {
  acquireSharedSocket,
  getOrCreateSharedSocket,
  getSharedPriceSocket,
  joinPriceRoom,
  leavePriceRoom,
  releaseSharedSocket,
} from "@/hooks/usePriceStream";

export interface PriceStrike {
  /** Numeric strike price (USD). */
  price: number;
  /** "reach" (target above) vs "dip" (target below). */
  side: "reach" | "dip";
  /** Short label for the line (e.g. "↑ $82K"). */
  label: string;
}

interface Props {
  /** Uppercase symbol (e.g. "BTC", "TSLA", "XAUUSD"). Cosmetic only. */
  symbol: string;
  /** WS-side symbol used for the socket subscription. For crypto this
   * is the Polymarket "btcusdt" style; for equity it's Pyth's
   * lowercase ticker ("tsla", "xauusd", "eurusd"). */
  polymarketSymbol: string;
  /** Deduped list of strikes to overlay. */
  strikes: PriceStrike[];
  /** Event resolution time (ISO). End of the history window. */
  endDate?: string | null;
  /** Event start time (ISO). Start of the history window. Falls back to
   * endDate - 24h. */
  eventStartTime?: string | null;
  /**
   * Asset class. Drives which Socket.IO event we listen to + whether
   * we fan-out to /api/polymarket/crypto-history for backfill. Default
   * "crypto" preserves the existing behaviour for callers that haven't
   * been updated. "equity" listens to `equity:price` (Pyth-fed stocks/
   * ETFs/forex/metals/commodities) and currently skips backfill — the
   * equity-history endpoint isn't on the backend yet, so we render
   * live ticks only and let the chart grow from cold start.
   */
  assetClass?: "crypto" | "equity";
}

const HEIGHT = 340;

/**
 * Price-level event chart that reads from the SAME oracle feed Polymarket
 * uses for resolution (Chainlink via our backend WS + /api/polymarket/crypto-history
 * for backfill). Renders an area chart of live price + one dashed
 * horizontal line per strike so the user can see exactly where the
 * resolution price sits versus each target.
 */
export default function CryptoPriceLevelChart({
  symbol,
  polymarketSymbol,
  strikes,
  endDate,
  eventStartTime,
  assetClass = "crypto",
}: Props) {
  const isEquity = assetClass === "equity";
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const areaRef = useRef<ISeriesApi<"Area"> | null>(null);
  const strikeLinesRef = useRef<ISeriesApi<"Line">[]>([]);
  const lastTimeRef = useRef<number>(0);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const sortedStrikes = useMemo(() => {
    const seen = new Set<string>();
    return [...strikes]
      .filter(s => Number.isFinite(s.price) && s.price > 0)
      .filter(s => {
        const key = `${s.side}:${s.price}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.price - b.price);
  }, [strikes]);

  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;

    const chart = createChart(container, {
      width: container.clientWidth,
      height: HEIGHT,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: isDark ? "#6B7280" : "#9CA3AF",
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: 11,
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { color: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.05)", style: LineStyle.Solid },
      },
      crosshair: {
        mode: CrosshairMode.Magnet,
        vertLine: { color: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)", width: 1, style: LineStyle.Dashed },
        horzLine: { color: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)", width: 1, style: LineStyle.Dashed },
      },
      rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.08, bottom: 0.08 } },
      timeScale: { borderVisible: false, timeVisible: true, secondsVisible: false, rightOffset: 6 },
      handleScroll: true,
      handleScale: true,
    });
    chartRef.current = chart;

    const lineColor = isDark ? "#FFFFFF" : "#111827";
    const area = chart.addSeries(AreaSeries, {
      lineColor,
      topColor: isDark ? "rgba(255,255,255,0.18)" : "rgba(17,24,39,0.12)",
      bottomColor: isDark ? "rgba(255,255,255,0.00)" : "rgba(17,24,39,0.00)",
      lineWidth: 2,
      priceLineVisible: true,
      priceLineColor: isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)",
      priceLineStyle: LineStyle.Dotted,
      priceLineWidth: 1,
      lastValueVisible: true,
      crosshairMarkerVisible: true,
      crosshairMarkerRadius: 3,
    });
    areaRef.current = area;

    const ro = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      if (width > 0 && chartRef.current) chartRef.current.resize(width, HEIGHT);
    });
    ro.observe(container);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      areaRef.current = null;
      strikeLinesRef.current = [];
    };
  }, [isDark]);

  // Backfill history from the oracle feed used for PM resolution.
  useEffect(() => {
    const area = areaRef.current;
    const chart = chartRef.current;
    if (!area || !chart) return;
    let cancelled = false;

    const load = async () => {
      try {
        // No equity-history endpoint yet — equity charts start empty
        // and grow as live ticks arrive. Skipping the fetch silently is
        // safer than 404-spamming the backend.
        if (isEquity) return;
        if (!endDate) return;
        const end = new Date(endDate);
        if (!Number.isFinite(end.getTime())) return;
        const start = eventStartTime
          ? new Date(eventStartTime)
          : new Date(end.getTime() - 24 * 60 * 60 * 1000);
        if (!Number.isFinite(start.getTime())) return;

        const durationHours = (end.getTime() - start.getTime()) / 3600000;
        // Confirmed PM variants: 5m→fiveminute, 15m→fifteen, 1h→hourly, 4h→4hourly.
        const variant = durationHours <= 1 ? "fiveminute"
          : durationHours <= 6 ? "fifteen"
          : durationHours <= 48 ? "hourly"
          : "4hourly";

        const qs = new URLSearchParams({
          symbol,
          eventStartTime: start.toISOString(),
          variant,
          endDate: end.toISOString(),
        });
        const res = await fetch(`/api/polymarket/crypto-history?${qs.toString()}`, { cache: "no-store" });
        if (!res.ok || cancelled) return;
        const points = (await res.json()) as Array<{ timestamp: number; value: number }>;
        if (!Array.isArray(points) || points.length === 0 || cancelled) return;

        const dedup = new Map<number, number>();
        for (const p of points) {
          const t = Math.floor(Number(p.timestamp) / 1000);
          const v = Number(p.value);
          if (Number.isFinite(t) && Number.isFinite(v) && v > 0) dedup.set(t, v);
        }
        const sorted = [...dedup.entries()].sort((a, b) => a[0] - b[0]);
        const data = sorted.map(([time, value]) => ({ time: time as Time, value }));
        area.setData(data);
        lastTimeRef.current = sorted[sorted.length - 1][0];
        chart.timeScale().fitContent();
      } catch {
        /* non-fatal */
      }
    };
    load();

    return () => { cancelled = true; };
  }, [symbol, eventStartTime, endDate, isEquity]);

  // Live ticks via shared Polymarket price socket — Chainlink preferred,
  // Binance allowed as silent-period fallback (same rule as CryptoPriceChart).
  // NOTE: the area series is created in a separate effect, so we look it
  // up inside `push` via the ref on every tick rather than at subscribe
  // time. Subscribing unconditionally also means the first render (when
  // the ref is still null) still opens the socket.
  useEffect(() => {
    if (!polymarketSymbol) return;

    let mounted = true;
    let socket: ReturnType<typeof getSharedPriceSocket> = null;
    let lastChainlink = 0;

    const push = (price: number) => {
      const area = areaRef.current;
      if (!area || !Number.isFinite(price) || price <= 0) return;
      const nowSec = Math.floor(Date.now() / 1000);
      if (nowSec < lastTimeRef.current) return;
      // Freeze the chart at resolution. Without this guard, every
      // crypto:price (or equity:price) WS tick continued to extend the
      // area past `endDate` with `time=nowSec`, so a resolved BTC 5m
      // market kept drawing live BTC into the future on the right side
      // — even though the "PRECIO FINAL" tarjeta was already frozen via
      // `CryptoPriceChart`'s own `resolved` gate. Stop pushing once
      // the round has settled; same component is reused for `equity`
      // assets so this guard covers Pyth-fed equities too.
      if (endDate && nowSec > Math.floor(new Date(endDate).getTime() / 1000)) return;
      area.update({ time: nowSec as Time, value: price });
      lastTimeRef.current = nowSec;
    };

    const cryptoHandler = (data: { symbol: string; price: number; source?: string }) => {
      if (!data || data.symbol !== polymarketSymbol || !(data.price > 0)) return;
      const now = Date.now();
      if (data.source === "chainlink") {
        lastChainlink = now;
        push(data.price);
        return;
      }
      if (data.source === "binance" && now - lastChainlink > 4000) {
        push(data.price);
      }
    };

    // Pyth feed is a single source — no chainlink/binance arbitration.
    // We still render `is_carried_forward` ticks because Pyth keeps
    // emitting the last value during market close and a flat line is
    // more useful than an empty chart.
    const equityHandler = (data: { symbol: string; price: number }) => {
      if (!data || data.symbol?.toLowerCase() !== polymarketSymbol.toLowerCase() || !(data.price > 0)) return;
      push(data.price);
    };

    const eventName = isEquity ? "equity:price" : "crypto:price";
    const room = isEquity ? "equity" : "crypto";
    const handler = isEquity ? equityHandler : cryptoHandler;
    let joinedRoom = false;

    const init = async () => {
      socket = getSharedPriceSocket() || (await getOrCreateSharedSocket());
      if (!socket || !mounted) return;
      acquireSharedSocket();
      socket.on(eventName, handler);
      // Join the crypto/equity measurement room (Deploy 1 room-scoping
      // instrument). No-op for data — the event is still broadcast globally.
      joinPriceRoom(room);
      joinedRoom = true;
    };
    init();

    return () => {
      mounted = false;
      if (!socket) return;
      socket.off(eventName, handler);
      if (joinedRoom) leavePriceRoom(room);
      releaseSharedSocket();
    };
  }, [polymarketSymbol, isEquity]);

  // Stable content-based key for strikes so the effect doesn't re-run every
  // time the parent passes a new array reference (e.g. React Query polling
  // re-builds `eventData.markets` → pmPriceLevelInfo → new strikes array
  // with identical contents). Without this, the effect was tearing down and
  // recreating every LineSeries on every poll, occasionally leaving the
  // chart with only the main price line.
  const strikesKey = useMemo(
    () => sortedStrikes.map(s => `${s.side}:${s.price}:${s.label}`).join('|'),
    [sortedStrikes]
  );

  // One dashed horizontal line per strike. Green for ↑ reach, red for ↓ dip.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    for (const s of strikeLinesRef.current) {
      try { chart.removeSeries(s); } catch { /* ignore */ }
    }
    strikeLinesRef.current = [];

    const now = Math.floor(Date.now() / 1000);
    const from = now - 24 * 3600;

    for (const strike of sortedStrikes) {
      const color = strike.side === "reach"
        ? (isDark ? "#10B981" : "#059669")
        : (isDark ? "#EF4444" : "#DC2626");
      const line = chart.addSeries(LineSeries, {
        color,
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        priceLineVisible: true,
        priceLineColor: color,
        priceLineStyle: LineStyle.Dashed,
        priceLineWidth: 1,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
        title: strike.label,
      });
      line.setData([
        { time: from as Time, value: strike.price },
        { time: now as Time, value: strike.price },
      ]);
      strikeLinesRef.current.push(line);
    }
    // sortedStrikes is intentionally left out — we rely on `strikesKey` for
    // content-based invalidation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strikesKey, isDark]);

  void symbol;

  return (
    <div className="bg-white dark:bg-[#18181D] border-t border-gray-200 dark:border-[#1A1A1A] overflow-hidden">
      <div ref={containerRef} style={{ width: "100%", height: HEIGHT }} />
    </div>
  );
}
